/**
 * L'effectif transmis aux assureurs — chunk 9.BK.3.
 *
 * ## Ce que cette étape refuse de laisser croire
 *
 * **Qu'un census verrouillé implique qu'un export existe.** Le verrou arrête
 * l'effectif ; il ne produit rien. Tant que le conseiller n'a pas cliqué, la
 * demande part sans la liste — et c'est exactement ce que cette étape doit
 * rendre visible plutôt que de le supposer réglé.
 *
 * ## Trois états, trois rendus
 *
 * `absent` — rien n'a été produit · `prêt` — le classeur reflète l'effectif
 * actuel · `périmé` — l'effectif a changé depuis. Jamais un seul rendu avec du
 * texte de remplacement : un écran qui dit la même chose de trois situations
 * apprend à ne plus être lu.
 *
 * ## L'écran ne juge rien
 *
 * L'état vient du serveur — de la **même** fonction qui refuse l'envoi. Le
 * recalculer ici finirait par diverger de celle qui décide, et l'écran
 * promettrait un envoi que la route refuse.
 *
 * ## Rien n'est régénéré en silence
 *
 * Un export périmé n'est pas remplacé tout seul : l'artefact est la preuve de ce
 * qui avait été préparé. L'écran le dit et propose le geste.
 */

import { useTranslation } from "react-i18next";

import { Etape } from "./Etape";

/** L'état tel que la readiness le rend. `null` quand le dossier n'a pas d'effectif. */
export type EtatExportEffectif = {
  readonly etat: "absent" | "pret" | "perime" | "inverifiable";
  readonly census_id: string;
  readonly artifact_file_id: string | null;
  readonly rendered_at: string | null;
};

export function EtapeEffectifAssureur(props: {
  readonly numero: number;
  readonly etatExport: EtatExportEffectif | null;
  readonly modifiable: boolean;
  readonly enCours: boolean;
  readonly onGenerer: () => void;
  readonly onTelecharger: (fileId: string) => void;
}): JSX.Element | null {
  const { t, i18n } = useTranslation();

  // Un dossier sans effectif n'a rien à transmettre : l'étape disparaît plutôt
  // que d'annoncer un vide. Doctrine §2.10 — un bloc qui n'a rien à dire ne
  // s'affiche pas.
  if (!props.etatExport) return null;

  const { etat, artifact_file_id, rendered_at } = props.etatExport;
  // `inverifiable` partage le rendu de `perime` — il faut agir dans les deux
  // cas — mais **pas le message** : l'un dit que l'effectif a changé, l'autre
  // qu'on ne peut pas le savoir. Le serveur les distingue ; l'écran aussi.
  const aRefaire = etat === "perime" || etat === "inverifiable";
  const etatEtape = etat === "pret" ? "ready" : aRefaire ? "stale" : "empty";

  const produitLe =
    rendered_at === null
      ? null
      : new Intl.DateTimeFormat(i18n.language, {
          dateStyle: "long",
          timeStyle: "short",
        }).format(new Date(rendered_at));

  return (
    <Etape
      numero={props.numero}
      titre={t("census_export.title")}
      description={t(
        props.modifiable ? "census_export.subtitle" : "census_export.history_subtitle",
      )}
      statusLabel={
        !props.modifiable && etat === "pret" ? t("census_export.history_ready") : undefined
      }
      etat={etatEtape}
    >
      {etat === "absent" ? (
        <div>
          <p className="text-sm text-slate-600">{t("census_export.absent_body")}</p>
          <button
            type="button"
            disabled={!props.modifiable || props.enCours}
            aria-busy={props.enCours}
            onClick={props.onGenerer}
            className="focus-visible:ring-brand-500 bg-brand-600 hover:bg-brand-700 mt-3 touch-manipulation rounded-lg px-3 py-1.5 text-sm font-semibold text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {props.enCours ? t("census_export.generating") : t("census_export.generate")}
          </button>
        </div>
      ) : null}

      {etat === "pret" ? (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-slate-700">
            {produitLe
              ? t("census_export.ready_body", { date: produitLe })
              : t("census_export.ready_body_undated")}
          </p>
          {artifact_file_id ? (
            <button
              type="button"
              onClick={() => props.onTelecharger(artifact_file_id)}
              className="focus-visible:ring-brand-500 touch-manipulation rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2"
            >
              {t("census_export.download")}
            </button>
          ) : null}
        </div>
      ) : null}

      {aRefaire ? (
        <div>
          {/* Le message dit CE QUI s'est passé et CE QU'IL FAUT FAIRE. « Périmé »
              seul laisserait chercher. */}
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {t(`census_export.${etat === "perime" ? "stale" : "unverifiable"}_body`)}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button
              type="button"
              disabled={!props.modifiable || props.enCours}
              aria-busy={props.enCours}
              onClick={props.onGenerer}
              className="focus-visible:ring-brand-500 bg-brand-600 hover:bg-brand-700 touch-manipulation rounded-lg px-3 py-1.5 text-sm font-semibold text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {props.enCours ? t("census_export.generating") : t("census_export.regenerate")}
            </button>
            {artifact_file_id ? (
              // L'ancien reste téléchargeable : c'est ce qui avait été préparé,
              // et le remplacer ne l'efface pas.
              <button
                type="button"
                onClick={() => props.onTelecharger(artifact_file_id)}
                className="focus-visible:ring-brand-500 touch-manipulation rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2"
              >
                {t("census_export.download_previous")}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </Etape>
  );
}
